import { useMemo } from "react";
import { safeParseJSON } from "./safeParseJSON";
import { useCurrentUser } from "../hooks/useCurrentUser";

enum Key {
  assignToMe = "assignToMe",
  assignedToUserId = "assignedToUserId",
  tenant = "tenant",
  userConfiguration = "userConfiguration",
  suggestedChasableFilters = "suggestedChasableFilters",
}

class SimpleLocalStorage {
  private static cache = new Map<string, SimpleLocalStorage>();

  static create(userId: string) {
    const instance = SimpleLocalStorage.cache.get(userId);
    if (instance) return instance;
    const newInstance = new SimpleLocalStorage(userId);
    SimpleLocalStorage.cache.set(userId, newInstance);
    return newInstance;
  }

  private readonly PREFIX = "attelas";

  private userId: string;

  readonly keys = Key;

  constructor(userId: string) {
    this.userId = userId;
    this.getItem = this.getItem.bind(this);
    this.setItem = this.setItem.bind(this);
    this.removeItem = this.removeItem.bind(this);
  }

  private getKey(key: Key) {
    return `${this.PREFIX}-${key}-${this.userId}`;
  }

  getItem<T>(key: Key): T | null {
    return safeParseJSON<T | null>(localStorage.getItem(this.getKey(key)), null);
  }

  setItem(key: Key, value: unknown) {
    localStorage.setItem(this.getKey(key), JSON.stringify(value));
  }

  removeItem(key: Key) {
    localStorage.removeItem(this.getKey(key));
  }
}

export function createLocalStorage(userId: string | null | undefined) {
  if (!userId) return null;
  return SimpleLocalStorage.create(userId);
}

export function useLocalStorage() {
  const { currentUser } = useCurrentUser();
  return useMemo(() => createLocalStorage(currentUser?.id), [currentUser?.id]);
}
